import { HttpClient, HttpContext, HttpErrorResponse, httpResource } from '@angular/common/http';
import { Service, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY, SKIP_SUCCESS_ALERT } from '../../../core/http/http-context-tokens';
import { AuthService } from '../../../core/services/auth/auth.service';
import { DependenteResponse, NovoDependente } from '../models/dependente.model';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/MeusDependentes`;

/** O erro fica local: o bloco "Dependentes" mostra a própria falha, sem o modal do interceptor. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Erro local e sem o alerta automático de sucesso: quem anuncia o resultado é a tela. */
const SILENCIOSO = () => ERRO_LOCAL().set(SKIP_SUCCESS_ALERT, true);

/** O que o `POST` carrega. Não há código de pessoa nem de usuário: o titular é o dono do token. */
export interface CorpoDependente {
  desNomeDpd: string;
  desNomeSocialDpd: string | null;
  desParentescoDpd: string;
  datNascimentoDpd: string;
  desGeneroDpd: string | null;
  desRacaCorDpd: string | null;
  desRgDpd: string | null;
  desCpfDpd: string | null;
}

/** O erro de uma gravação, pronto para a tela: a frase e, quando houver, as violações. */
export interface ErroDependente {
  mensagem: string;
  detalhes: string[];
}

/**
 * Os dependentes de quem está logado: lista, cadastro e remoção em
 * `api/sai/cartaservico/MeusDependentes`.
 *
 * A lista só é buscada com sessão, e depende do usuário: cada login a refaz, e o logout a
 * esquece — os dependentes da pessoa anterior não ficam na memória.
 *
 * ⚠️ As gravações saem **silenciosas** (erro local e sem alerta de sucesso). O cadastro é feito
 * de dentro de um `<dialog>` aberto com `showModal()`, que deixa o SweetAlert do interceptor
 * atrás do modal e sem clique: o erro aparece dentro do modal, e o sucesso só depois de fechá-lo.
 */
@Service()
export class MeusDependentesService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly usuario = toSignal(this.auth.currentUser, {
    initialValue: this.auth.currentUserValue,
  });

  private readonly listaApi = httpResource<ResponseApi<DependenteResponse[]>>(() => {
    // `undefined` é "nada a buscar": sem esta guarda a chamada sairia sem token e voltaria 401.
    if (!this.usuario()?.token) return undefined;

    return { url: RECURSO, context: ERRO_LOCAL() };
  });

  readonly falhou = computed(() => this.listaApi.error() !== undefined);

  /**
   * A lista, ou `null` sem sessão, na primeira carga ou em falha. Durante o `reload()` de
   * depois de uma gravação o valor anterior fica — a lista não pisca "carregando".
   *
   * O `error()` é consultado ANTES do `value()` de propósito: em estado de erro o `value()`
   * **lança**.
   */
  readonly dependentes = computed<readonly DependenteResponse[] | null>(() => {
    if (this.listaApi.error() !== undefined) return null;

    return this.listaApi.value()?.data ?? null;
  });

  /** Refaz a chamada — é o "Tentar novamente" do aviso de falha. */
  recarregar(): void {
    this.listaApi.reload();
  }

  adicionar(novo: NovoDependente): Observable<DependenteResponse> {
    return this.http
      .post<ResponseApi<DependenteResponse>>(RECURSO, corpoDoDependente(novo), { context: SILENCIOSO() })
      .pipe(
        map((resposta) => resposta.data),
        tap(() => this.listaApi.reload()),
      );
  }

  remover(codDependenteDpd: number): Observable<void> {
    return this.http
      .delete<ResponseApi<DependenteResponse>>(`${RECURSO}/${codDependenteDpd}`, { context: SILENCIOSO() })
      .pipe(
        tap(() => this.listaApi.reload()),
        map(() => undefined),
      );
  }
}

/**
 * O formulário do modal no contrato da API.
 *
 * Função pura e exportada: é o ponto em que o contrato do back-end é honrado. Textos aparados,
 * opcional vazio como `null` e o CPF só com os dígitos (a máscara é da tela).
 */
export function corpoDoDependente(novo: NovoDependente): CorpoDependente {
  const digitosCpf = novo.cpf.replace(/\D/g, '');

  return {
    desNomeDpd: novo.nomeCompleto.trim(),
    desNomeSocialDpd: opcional(novo.nomeSocial),
    desParentescoDpd: novo.parentesco.trim(),
    datNascimentoDpd: novo.dataNascimento,
    desGeneroDpd: opcional(novo.genero),
    desRacaCorDpd: opcional(novo.racaCor),
    desRgDpd: opcional(novo.rg),
    desCpfDpd: digitosCpf || null,
  };
}

/**
 * Traduz o erro HTTP de uma gravação para o que a tela mostra.
 *
 * A `message` da API só é usada em `400` e `404`, onde ela é a regra recusada. Em `5xx` e sem
 * resposta ela **nunca** é mostrada: num erro de banco traz o texto do SQL Server, com o nome
 * do índice e a chave duplicada.
 */
export function mensagemErroDependente(erro: unknown, acao: 'salvar' | 'remover'): ErroDependente {
  const resposta = erro instanceof HttpErrorResponse ? erro : null;
  const corpo = (resposta?.error ?? null) as Partial<ResponseApi<unknown>> | null;

  const generica = `Não foi possível ${acao} o dependente agora. Tente novamente em instantes.`;

  switch (resposta?.status) {
    case 400:
    case 404: {
      const mensagemApi = typeof corpo?.message === 'string' ? corpo.message.trim() : '';

      // A mensagem costuma repetir a única violação; sem o filtro ela apareceria duas vezes.
      const detalhes = (Array.isArray(corpo?.errors) ? corpo.errors : [])
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .map((item) => item.trim())
        .filter((item) => item !== mensagemApi);

      return { mensagem: mensagemApi || generica, detalhes };
    }

    case 401:
      // O interceptor já encerrou a sessão; a aba mostra "Sua sessão foi encerrada".
      return { mensagem: 'Sua sessão expirou. Entre novamente.', detalhes: [] };

    default:
      return { mensagem: generica, detalhes: [] };
  }
}

function opcional(valor: string): string | null {
  return valor.trim() || null;
}
